import React, { useState, useEffect, useMemo, useCallback } from 'react';
import PasswordModal from './PasswordModal';
import { BACKEND_URL, apiFetch } from '../constants';
import MapView from './MapView';
import LayerPanel from './LayerPanel';
import PowerFlowReports from './PowerFlowReports';


export default function ResultPage({ task }) {
  const taskId   = task?.id;
  const [projectName, setProjectName] = useState(task?.name || taskId || 'Unnamed task');

  const [tiles,          setTiles]          = useState({});
  const [downloads,      setDownloads]      = useState({});
  const [prints,         setPrints]         = useState({});
  const [grids,          setGrids]          = useState({});
  const [urls,           setUrls]           = useState({ prints: {}, tiles: {} });
  const [layerVisibility, setLayerVisibility] = useState({
    lv_grid: true, mv_grid: true, buildings: true,
  });
  const [modalOpen,  setModalOpen]  = useState(false);
  const [injectDone, setInjectDone] = useState(false);
  const [injecting,  setInjecting]  = useState(false);
  const [stepRunning, setStepRunning] = useState(null);
  const [viewMode, setViewMode] = useState('map');       // 'map' | 'reports'
  const [activeReport, setActiveReport] = useState(null); // 'general'|'lv'|'mv'|'power'
  const [highlightedGrid, setHighlightedGrid] = useState(null);

  const tileUrlsMemo = useMemo(() => urls.tiles || {}, [urls.tiles]);

  useEffect(() => {
    if (!taskId) return;
    const controller = new AbortController();
    (async () => {
      try {
        const res = await apiFetch(
          `${BACKEND_URL}/tasks/${encodeURIComponent(taskId)}/manifest`,
          { signal: controller.signal }
        );
        if (!res.ok) throw new Error('manifest not found');
        const data = await res.json();
        if (data.status !== 'ok') throw new Error('bad manifest');
        setPrints(data.prints || {});
        setDownloads(data.downloads || {});
        setTiles(data.tiles || {});
        setGrids(data.grids || {});
        setUrls(data.urls || {});
        if (data.name) setProjectName(data.name);
      } catch (e) {
        if (e.name === 'AbortError') return;
        console.error('Failed to load manifest', e);
      }
    })();
    return () => controller.abort();
  }, [taskId]);

  const handleInject = async () => {
    if (!taskId) return;
    setInjecting(true);
    try {
      const res = await apiFetch(`${BACKEND_URL}/insert-into-db`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ task_id: taskId }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'inject failed');
      setInjectDone(true);
      setModalOpen(false);
    } catch (err) {
      console.error('Failed to inject results', err);
      alert(err.message || 'Failed to inject results. Please try again.');
    } finally {
      setInjecting(false);
    }
  };

  const runStep = useCallback(async (stepKey) => {
    if (!taskId || stepRunning) return;
    setStepRunning(stepKey);
    try {
      const res = await apiFetch(`${BACKEND_URL}/continue-generation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ task_id: taskId, step: stepKey }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || `Server error ${res.status}`);
      }
      // Reload manifest after a short delay so tiles reflect the new state
      setTimeout(async () => {
        try {
          const r = await apiFetch(`${BACKEND_URL}/tasks/${encodeURIComponent(taskId)}/manifest`);
          if (r.ok) {
            const d = await r.json();
            if (d.status === 'ok') {
              setPrints(d.prints || {});
              setDownloads(d.downloads || {});
              setTiles(d.tiles || {});
              setGrids(d.grids || {});
              setUrls(d.urls || {});
            }
          }
        } catch (_) {}
        setStepRunning(null);
      }, 3000);
    } catch (err) {
      console.error('Step error:', err);
      alert(`Could not start step: ${err.message}`);
      setStepRunning(null);
    }
  }, [taskId, stepRunning]);

  const handleGridClick = useCallback((gridKey) => {
    setHighlightedGrid(prev => prev === gridKey ? null : gridKey);
  }, []);

  const handleToggleView = useCallback((mode) => {
    setViewMode(mode);
    if (mode === 'reports' && !activeReport) {
      // Match the order in LayerPanel: MV first, then LV, general, power
      const first = ['mv', 'lv', 'power'].find(k => prints[k]);
      setActiveReport(first || null);
    }
  }, [prints, activeReport]);

  const reportUrl = useMemo(() => {
    if (!activeReport || !urls.prints?.[activeReport]) return null;
    return `${BACKEND_URL}${urls.prints[activeReport]}`;
  }, [activeReport, urls.prints]);

  // NetLab opens in its own tab on the NetBank picker for this area, with the
  // grid highlighted on the map (keys look like 'lv:TR_123' / 'mv:TR_MV_1') preselected.
  const openNetlab = useCallback(() => {
    const params = new URLSearchParams({ mode: 'netlab', task: taskId });
    const [level, grid] = (highlightedGrid || '').split(/:(.*)/s);
    if (grid) { params.set('level', level); params.set('grid', grid); }
    window.open(`?${params.toString()}`, '_blank', 'noopener');
  }, [taskId, highlightedGrid]);

  const continueSteps = useMemo(() => {
    const bothGrids = grids.lv && grids.mv;
    return [{ key: 'auto', label: bothGrids ? 'Update maps' : 'Continue generation' }];
  }, [grids]);

  return (
    <div style={{ position: 'relative', width: '100%', height: '100vh', background: '#0f172a' }}>

      {/* 3D map — always mounted so deck.gl state survives view switching */}
      <div style={{ position: 'absolute', inset: 0, visibility: viewMode === 'map' ? 'visible' : 'hidden' }}>
        <MapView
          backendUrl={BACKEND_URL}
          tileUrls={tileUrlsMemo}
          layerVisibility={layerVisibility}
          highlightedGrid={highlightedGrid}
          onGridClick={handleGridClick}
        />
      </div>

      {/* Reports — Plotly dashboard for power flow, iframe for everything else */}
      {viewMode === 'reports' && (
        <div style={{ position: 'absolute', inset: 0 }}>
          {activeReport === 'power' ? (
            <PowerFlowReports taskId={taskId} backendUrl={BACKEND_URL} />
          ) : reportUrl ? (
            <iframe
              key={reportUrl}
              src={reportUrl}
              style={{ width: '100%', height: '100%', border: 'none', background: '#fff' }}
              title="Report"
            />
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#475569', fontSize: 14 }}>
              No reports available. Run the generation steps first.
            </div>
          )}
        </div>
      )}

      <LayerPanel
        visibility={layerVisibility}
        onChange={setLayerVisibility}
        tileAvailability={tiles}
        taskName={projectName}
        hasOutput={!!downloads.available}
        downloadUrl={urls.download}
        backendUrl={BACKEND_URL}
        onInject={() => setModalOpen(true)}
        injectDone={injectDone}
        injecting={injecting}
        continueSteps={continueSteps}
        onRunStep={runStep}
        stepRunning={stepRunning}
        viewMode={viewMode}
        onToggleView={handleToggleView}
        prints={prints}
        activeReport={activeReport}
        onSelectReport={setActiveReport}
        onOpenNetlab={taskId && (grids.lv || grids.mv) ? openNetlab : undefined}
        netlabGrid={highlightedGrid ? highlightedGrid.split(':').slice(1).join(':') : null}
      />
      <PasswordModal
        open={modalOpen}
        onSubmit={handleInject}
        onClose={() => setModalOpen(false)}
        checkPassword={pwd => pwd === 'netbank'}
      />
    </div>
  );
}